import { useEffect, useState } from "react";

// The visitor's last demo on this browser, so the demo can offer "View your results" when they
// come back to tryapex25.com (a different site from recruit.theresume.io, so nothing is shared
// with the account). Kept only until the server deletes the session.
const STORAGE_KEY = "a25-last-demo";

export type LastDemo = {
  sessionId: string;
  roleId: string;
  expiresAt: string;
  // Which results link to use: sign-up until they pick "Sign in".
  auth: "signup" | "signin";
};

export const readLastDemo = (now = Date.now()): LastDemo | null => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null") as LastDemo | null;
    if (!stored?.sessionId || !(Date.parse(stored.expiresAt) > now)) return null;
    return stored;
  } catch {
    return null;
  }
};

export const rememberLastDemo = (demo: LastDemo) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(demo));
  } catch {
    // Storage blocked: the email and the dashboard still lead back.
  }
};

export const setLastDemoAuth = (auth: LastDemo["auth"]) => {
  const current = readLastDemo();
  if (current) rememberLastDemo({ ...current, auth });
};

/** "1 h 32 min", "45 min", "1 min" */
export const formatTimeLeft = (expiresAt: string, now = Date.now()) => {
  const minutes = Math.max(Math.ceil((Date.parse(expiresAt) - now) / 60_000), 1);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest} min`;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
};

/** "3:40 pm", in the visitor's own time zone. */
export const formatUntil = (expiresAt: string) =>
  new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(
    new Date(expiresAt)
  );

/** Re-renders every 30 seconds while `active`, for countdowns. */
export const useNow = (active: boolean) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const interval = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(interval);
  }, [active]);
  return now;
};
